import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { paths, generatePathDatacentral } from '@/utils/path'
import type { LinkFormatterConfig } from './types'

/**
 * Render link formatter
 */
export function renderLinkFormatter<T = any>(
    value: any,
    row: T,
    config: LinkFormatterConfig<T>
): ReactNode {
    const {
        external,
        target,
        className,
        icon,
        iconPosition = 'after',
        label,
    } = config

    // Generate URL based on config type
    let url: string

    if ('pathKey' in config) {
        // Internal link using paths helper (RECOMMENDED)
        const pathTemplate = paths[config.pathKey]

        // Get path params
        let pathParams: any = {}
        if (config.pathParams) {
            pathParams =
                typeof config.pathParams === 'function'
                    ? config.pathParams(value, row)
                    : config.pathParams
        }

        // Get query params
        let queryParams: any = undefined
        if (config.query) {
            queryParams =
                typeof config.query === 'function'
                    ? config.query(value, row)
                    : config.query
        }

        // Generate path using helper
        url = generatePathDatacentral(
            pathTemplate as any,
            pathParams,
            queryParams
        )
    } else {
        // External/custom link using href
        url =
            typeof config.href === 'function'
                ? config.href(value, row)
                : config.href
    }

    // Use custom label if provided, otherwise use value or url
    const displayValue = label
        ? typeof label === 'function'
            ? label(value, row)
            : label
        : value || url

    const linkContent = (
        <>
            {icon && iconPosition === 'before' && (
                <span className="mr-1">{icon}</span>
            )}
            {displayValue}
            {icon && iconPosition === 'after' && (
                <span className="ml-1">{icon}</span>
            )}
        </>
    )

    if (external) {
        return (
            <a
                href={url}
                target={target}
                rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                className={cn(
                    'text-primary hover:text-primary-hover hover:underline',
                    className
                )}
            >
                {linkContent}
            </a>
        )
    }

    return (
        <Link
            to={url}
            className={cn(
                'text-primary hover:text-primary-hover hover:underline',
                className
            )}
        >
            {linkContent}
        </Link>
    )
}
